import { ComponentRef, ErrorHandler, Injectable, Injector, OnDestroy } from '@angular/core';
import { LogLevelEnum } from '@infrastructure/logLevel.enum';
import { NotfoundComponent } from '@modals/notfound.component';
import { Observable, Subscription } from 'rxjs';
import { ErrorComponent } from '../_modals/error.component';
import { EventsService } from './events.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { LogEvent, LoggerService } from './logger.service';
import { LoginService } from './login.service';
import { ModalService } from './modal.service';
import { UserService } from './user.service';
import { ErrorService } from './error.service';

@Injectable({
	providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler, OnDestroy {

	private modal: Observable<ComponentRef<{}>> | null;
	private subscriptions: Array<Subscription> = [];

	constructor(
		public injector: Injector,
		public eventService: EventsService,
		private googleAnalyticsService: GoogleAnalyticsService,
		private userService: UserService,
		private errorService: ErrorService,
		private loggerService: LoggerService) {
		this.errorService.errors.subscribe(async err => {
			if(!err) {
				return;
			}

			const error = err.rejection || err.originalError || err; // find the real error message if wrapped from promise
			this.eventService.showLoader(false);

			const user = await this.userService.GetUser();
			let uuid = '<unknown user>';

			if (user) {
				uuid = user.Uuid;
			}

			if (/Loading chunk [\d]+ failed/.test(err.message)) {
				await this.googleAnalyticsService.eventEmitter('error', 'engagement', `${uuid} encountered an error: Loading chunk failed => reload page`, 0);

				window.location.reload();
			}
			else if (error.name === 'IncompatibleBrowserError') {
				console.error('Incompatible browser detected by ErrorHandler:', error.message, error.details);
				const properties = {
					'appIdentifier': 'Customiser Web Application',
					'errorOrigin': 'useragent-detection.js',
					'errorName': error.name,
					'userAgentString': error.details?.userAgent,
					'detectedBrowser': error.details?.detectedBrowser,
					'detectedVersion': error.details?.detectedVersion,
					// Add any other custom properties from error.details if needed
					...(error.details || {})
				};				
				const payload = new LogEvent(
					error.message,
					LogLevelEnum.Error,
					properties
				);
				await this.loggerService.sendClientLog(payload);
				// Optionally, you might want to display a specific message to the user here
				// For now, it will fall through to the generic error modal if not handled by other specific conditions
			}
			else if (error.url && error.url.toLowerCase().includes('orderitem/create')) {
				console.error('Order item creation error:', error);
			}
			else if (error.status && (error.status === 401 || error.status === 403)) { // user is not authenticated anymore
				await this.googleAnalyticsService.eventEmitter('error', 'engagement', `${uuid} is not authenticated anymore => Logout => /login`, 0);

				const loginService = this.injector.get(LoginService);

				if (!loginService.AllowUnauthenticatedAccess()) {
					await loginService.Logout(false);
					location.href = location.origin + "/login";
					return;
				}
			}
			else if (error.status && error.status === 404) { // endpoint responded with 404
				const errorKey = error.status || '[N/A]';
				const errorMessage: string = error?.error?.Message;
				const modalService = this.injector.get(ModalService);

				if (!this.modal) {
					this.modal = modalService.create(NotfoundComponent, { errorKey, errorMessage });

					this.subscriptions.push(this.modal.subscribe((ref) => {
						this.eventService.showLoader(false);

						if (ref.hostView.destroyed) {
							this.modal = null;
						}
					}));
				}
			}
			else if (error.name == 'ChunkLoadError') { //this specific chunk load error can happen when suddenly offline while navigating the customiser.

				const modalService = this.injector.get(ModalService);
				await this.googleAnalyticsService.eventEmitter('error', 'engagement', `${uuid} seems to offline => offlineModal`, 0);

				this.modal = modalService.create(ErrorComponent);

				this.subscriptions.push(this.modal.subscribe((ref) => {
					if (ref.hostView.destroyed) {
						this.modal = null;
					}
				}));
			}
			else {
				console.error("GlobalErrorHandler", error);

				// no status == app error, status == api error
				const errorKey = error.status || '[N/A]';
				const errorMessageTechnical: string = error?.message;

				const modalService = this.injector.get(ModalService);

				if (!this.modal) {
					this.modal = modalService.create(ErrorComponent, { errorKey, errorMessageTechnical });

					let userId = 'Anonymous';

					if (user) {
						userId = user.CustomerNo + ' - ' + user.Name + ' (' + user.DeliveryEmail + ')';

						if (user.AliasImitator !== '') {
							userId = `${user.AliasImitator} @$ ${userId}`
						}

						await this.googleAnalyticsService.eventEmitter('error', 'engagement', `${user.Uuid} encountered an ${errorKey ? 'app error: ' + errorKey : 'api error'} (${window.location})`, 0);
					}

					try {
						let payload = new LogEvent(
							error.message,
							LogLevelEnum.Error,
							{
								'appIdentifier': 'Customiser Web Application',
								'errorOrigin': window.location.href,
								'stackTrace': error.stack
							}
						);

						await this.loggerService.sendClientLog(payload);
					}
					catch (comError) {
						console.error("Error in sendClientException", comError);
					}

					this.subscriptions.push(
						this.modal.subscribe((ref) => {
							this.eventService.showLoader(false);

							if (ref.hostView.destroyed) {
								this.modal = null;
							}
						})
					);
				}
			}

			// Rethrow the error to pass it along to the calling code
			throw error;
		});
	}

	handleError(error: any) {
		if (error.url && error.url.includes('assets/')) {
			return;
		}

		this.errorService.setError(error);
	}

	ngOnDestroy() {
		this.subscriptions.forEach(x => x.unsubscribe());
		this.errorService.errors.unsubscribe();
	}
}